import { Link, MessageBar, MessageBarType, Spinner } from "@fluentui/react";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
// import { useLocation } from "react-router-dom"; // ESLint: import mai utilizzato
import AlarmGalleryHandler from "./Components/AlarmGalleryHandler/AlarmGalleryHandler";
// import NavigationMenu from "./Components/NavigationMenu/NavigationMenu"; // ESLint: import mai utilizzato
import { AppRoutesMap, IAppRoutesMapItem } from "./Constants/AppRoutesMap";
// import { IAlarm } from "./Models/IAlarm"; // ESLint: import mai utilizzato
import { GlobalState } from "./Redux/RootReducer";
import { IAPIResponse } from "./Services/AjaxService";
import ApiService from "./Services/ApiService";
import { AppRoutes } from "./Constants/AppRoutes";
import { GenericActions } from "./Redux/Generic/GenericAction";
import { GetProcessParam } from "./Utils/ProcessParams";
import WebSocketService from "./Services/WebSocketService";
import { isWebSocketEnabled, getSentinelPollingInterval } from "./Config/AppConfig";

const AppLayout = () => {
  const dispatch = useDispatch();
  // const location = useLocation(); // ESLint: variabile mai utilizzata
  const loggedUser = useSelector((s: GlobalState) => s.generic.user);
  const sysSettings = useSelector((s: GlobalState) => s.generic.systemSettings);
  const [loadingSettings, setLoadingSettings] = useState<boolean>(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [routes, setRoutes] = useState<IAppRoutesMapItem[]>(
    AppRoutesMap.filter((x: IAppRoutesMapItem) => x.requiresLogin === false)
  );
  // OTTIMIZZATO: Traccia se ci sono allarmi attivi per polling intelligente
  // const [hasActiveAlarms, setHasActiveAlarms] = useState<boolean>(false); // ESLint: variabili mai utilizzate
  // Traccia se c'è un errore di autenticazione in corso
  const [isAuthError, setIsAuthError] = useState<boolean>(false);
  // ⚠️ FIX CRITICO: useRef per evitare loop multipli sovrapposti (Code:181225)
  const pollingTimeoutRef = useRef<number | null>(null);
  const isPollingActiveRef = useRef<boolean>(false);
  const pollingInitializedRef = useRef<boolean>(false); // ⚠️ FIX: Flag per evitare re-inizializzazioni

  const routeHasAtLeastOne = (required: string[]) => {
    let roles: any[] = [];
    if (loggedUser) {
      roles = loggedUser.roles;
    }

    if (required.length === 0) {
      return true;
    }
    for (let i = 0; i < required.length; i++) {
      if (roles.filter((x) => required[i] === x.type).length > 0) {
        return true;
      }
    }
    return false;
  };

  const loadSysSettings = () => {
    setLoadingSettings(true);
    setTimeout(() => {
      ApiService.SystemSettingController.GetSettingsList(
        (response: IAPIResponse) => {
          if (response.error === null) {
            dispatch(GenericActions.setSysSettings(response.parsed) as any);
          } else {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
            setCriticalError(true);
          }
          setLoadingSettings(false);
        }
      );
    }, 2000);
  };

  const loadSentinelsNow = () => {
    const sessionToken = localStorage.getItem("sessionToken");
    
    // Non fare richieste se c'è un errore di autenticazione in corso o manca il token
    if (isAuthError || !sessionToken || !loggedUser) {
      console.log("Polling sentinels fermato: errore di autenticazione, token mancante o utente non loggato");
      return;
    }

    // Verifica che non siamo sulla pagina di login
    if (window.location.pathname.includes(AppRoutes.APP_LOGIN_PAGE)) {
      console.log("Polling sentinels fermato: pagina di login");
      return;
    }

    ApiService.SentinelController.GetSentinelsPoll(
      (response: IAPIResponse) => {
        if (response.error === null) {
          dispatch(GenericActions.setSentinels(response.parsed) as any);
          // Se la richiesta ha successo, resetta lo stato di errore
          if (isAuthError) {
            setIsAuthError(false);
            console.log("Polling ripreso: autenticazione recuperata");
          }
        } else if (response.raw && response.raw.status === 401) {
          // Errore 401: token invalido o scaduto
          if (!isAuthError) {
            console.log("Polling sospeso: token invalido o scaduto");
            setIsAuthError(true);
          }
        }
      }
    );
  };

  const loopLoadSentinels = () => {
    // 🛡️ GUARD: Previeni loop multipli sovrapposti
    if (isPollingActiveRef.current) {
      console.warn('⚠️ [AppLayout] Loop sentinels già attivo, skip creazione nuovo timeout');
      return;
    }

    // Cancella timeout precedente prima di crearne uno nuovo
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    // ⚠️ FIX CRITICO: Usa AppConfig invece di GetProcessParam (Code:181225)
    // GetProcessParam legge da Redux systemSettings che potrebbero non essere ancora caricati
    // AppConfig legge direttamente da process.env con fallback
    const baseInterval = getSentinelPollingInterval(); // Default: 10000ms
    
    // Aumenta il delay se c'è un errore di autenticazione (backoff)
    const finalDelay = isAuthError ? baseInterval * 5 : baseInterval;
    
    isPollingActiveRef.current = true;
    console.log(`🔄 [AppLayout] Avvio polling sentinels (intervallo: ${finalDelay}ms)`);
    
    const timeoutId = setTimeout(() => {
      loadSentinelsNow();
      isPollingActiveRef.current = false; // Reset guard prima della ricorsione
      loopLoadSentinels();
    }, finalDelay) as unknown as number;
    
    pollingTimeoutRef.current = timeoutId;
  };

  // ⚠️ FIX: useEffect SEPARATO per caricare le impostazioni SOLO al login
  // Si attiva quando loggedUser cambia da undefined a definito (login effettivo)
  // NON si riattiva quando il componente si ri-renderizza per altri motivi (es. controlli audio)
  useEffect(() => {
    // Carica le impostazioni SOLO se:
    // 1. L'utente è loggato
    // 2. Le impostazioni non sono già state caricate
    // 3. Non c'è un caricamento già in corso
    if (loggedUser !== undefined && sysSettings.length === 0 && !loadingSettings) {
      console.log("🔧 Caricamento impostazioni di sistema al login...");
      loadSysSettings();
    }
    
    // Al logout (loggedUser diventa undefined), resetta le impostazioni di sistema
    if (loggedUser === undefined && sysSettings.length > 0) {
      console.log("🧹 Pulizia impostazioni di sistema al logout...");
      dispatch(GenericActions.setSysSettings([]) as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser]); // Dipende SOLO da loggedUser, si attiva solo al cambio di login/logout

  // ⚠️ FIX CRITICO: useEffect senza dipendenze per evitare loop infinito (Code:181225)
  useEffect(() => {
    // Cleanup function per fermare il polling
    const stopPolling = () => {
      if (pollingTimeoutRef.current !== null) {
        console.log("🛑 [AppLayout] Stop polling sentinels");
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
        isPollingActiveRef.current = false;
        pollingInitializedRef.current = false; // Reset flag
      }
    };

    // ⚠️ FIX CRITICO: Esegui SOLO se utente loggato E polling non già inizializzato
    if (loggedUser !== undefined && !pollingInitializedRef.current) {
      pollingInitializedRef.current = true; // Marca come inizializzato
      console.log('🚀 [AppLayout] Inizializzazione polling sentinels (PRIMA VOLTA)');
      setRoutes([...AppRoutesMap]);
      ApiService.UsersController.GetUsersList((response: IAPIResponse) => {
        if (response.error === null) {
          dispatch(GenericActions.setUsers(response.parsed));
        }
      });

      const sessionToken = localStorage.getItem("sessionToken");
      
      // ⚠️ FIX CRITICO: Connetti WebSocket se abilitato (Code:181225)
      if (isWebSocketEnabled() && sessionToken) {
        console.log('🔌 [AppLayout] Connessione WebSocket per aggiornamenti sentinels...');
        
        // ✅ FIX: Chiama connect() con il token JWT (MANCAVA!)
        WebSocketService.connect(sessionToken);
        
        // ⚠️ FIX: Aspetta 5 secondi per verificare se WebSocket si connette
        // Se WebSocket fallisce, avvia HTTP polling come fallback
        setTimeout(() => {
          if (!WebSocketService.isSocketConnected()) {
            console.warn('⚠️ [AppLayout] WebSocket non connesso dopo 5s. Avvio fallback HTTP polling per sentinels...');
            loopLoadSentinels(); // Avvia polling SOLO se WebSocket fallisce
          } else {
            console.log('✅ [AppLayout] WebSocket connesso. HTTP polling sentinels disabilitato per ridurre carico.');
          }
        }, 5000);
      } else {
        // WebSocket disabilitato → usa HTTP polling
        console.log('📡 [AppLayout] WebSocket disabilitato. Uso HTTP polling per sentinels.');
        loopLoadSentinels();
      }

      // Listener per aggiornare immediatamente i sentinels quando viene gestito un allarme
      const handleAlarmReload = () => {
        loadSentinelsNow();
      };

      // Listener per riprendere il polling dopo il refresh del token
      const handleTokenRefreshed = () => {
        console.log("Token refreshed, riprendendo il polling");
        setIsAuthError(false);
      };

      document.addEventListener("reload-all-alarms", handleAlarmReload);
      document.addEventListener("token-refreshed", handleTokenRefreshed);

      return () => {
        document.removeEventListener("reload-all-alarms", handleAlarmReload);
        document.removeEventListener("token-refreshed", handleTokenRefreshed);
        // Cleanup: ferma il polling quando il componente viene smontato
        stopPolling();
      };
    } else if (loggedUser === undefined && pollingInitializedRef.current) {
      // Se l'utente si disconnette, ferma il polling
      console.log('👋 [AppLayout] Logout rilevato, stop polling');
      stopPolling();
    }
  }, [loggedUser]); // ⚠️ Mantieni dipendenza per rilevare login/logout

  return (
    <div>
      <Routes>
        {routes.map((x: IAppRoutesMapItem, i: number) => {
          return (
            <Route
              path={x.path}
              element={
                <div>
                  {!x.requiresLogin && <div>{x.element}</div>}
                  {x.requiresLogin && (
                    <div>
                      {criticalError && !loadingSettings && (
                        <div>
                          <div className="bad-error-title">
                            <strong>Errore critico: </strong>
                            <br />
                            il sistema non può essere avviato.
                          </div>
                          <div className="bad-error-image">
                            <img src="./assets/error-bot.png" alt="Errore critico" />
                          </div>
                          <div className="bad-error-description">
                            Non è stato possibile scaricare la configurazione
                            dei parametri di sistema dal server remoto. <br />
                            <br />
                            <small>
                              Suggeriamo di eseguire il refresh della pagina. Se
                              questo errore persiste, contattare il supporto
                              tencnico.
                            </small>
                            <br />
                            <br />
                            <Link href={AppRoutes.APP_LOGOUT_PAGE}>Logout</Link>
                          </div>
                        </div>
                      )}
                      {loadingSettings && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100vh",
                            justifyContent: "space-around",
                            transform: "scale(2)",
                          }}
                        >
                          <Spinner label="Caricamento..." />
                        </div>
                      )}
                      {!criticalError && !loadingSettings && (
                        <div style={{ display: "flex" }}>
                          {routeHasAtLeastOne(x.requiresRoles) && (
                            <div style={{ display: "flex", width: "100%" }}>
                              <img
                                className="image-whole-back"
                                src="./assets/banner-green.jpg"
                                alt="Banner"
                              />
                              <AlarmGalleryHandler />
                              {x.element}
                            </div>
                          )}
                          {!routeHasAtLeastOne(x.requiresRoles) && (
                            <div
                              style={{
                                padding: "2em",
                                width: "100%",
                              }}
                            >
                              <MessageBar messageBarType={MessageBarType.error}>
                                Non disponi dei permessi necessari a
                                visualizzare questa pagina.{" "}
                                <Link href={AppRoutes.APP_LOGOUT_PAGE}>
                                  Logout
                                </Link>
                              </MessageBar>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              }
              key={i}
            />
          );
        })}
      </Routes>
    </div>
  );
};

export default AppLayout;
